import { useEffect, useMemo, useState } from "react";

import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import {
  mapFhirPatientToCrmPatientSummary,
  mapPatientEntriesToCrmPatientSummaries,
} from "src-next/modules/crm-clinical/communications/helpers/mapFhirPatientToCrmPatientSummary";
import type { CrmPatientSummary } from "src-next/modules/crm-clinical/communications/types";
import { consentPatientsNotAttended } from "src-next/modules/organization-patients";

export interface ConsentNotAttendedRow {
  patientId: string;
  name: string;
  patient: fhir5.Patient;
}

/**
 * Quem consentiu e não está nos atendidos.
 * O pedido de consentimento só acontece quando este hook monta
 * (o card entra em visitedAnalysisIds). A lista bruta do contexto não muda.
 */
export function useConsentNotAttendedPatients(): {
  rows: ConsentNotAttendedRow[];
  loading: boolean;
} {
  const context = usePatientOrganizationContext();
  const requestConsentPatients = context?.requestConsentPatients;
  const [asked, setAsked] = useState(false);

  useEffect(() => {
    requestConsentPatients?.();
    setAsked(true);
  }, [requestConsentPatients]);

  const attendedLoading = Boolean(context?.patientOrganizationLoading);
  const consentLoading = Boolean(context?.consentPatientsLoading);
  const loading = !asked || attendedLoading || consentLoading;

  const rows = useMemo(() => {
    if (loading || !context) return [];

    const patientById = new Map<string, fhir5.Patient>();
    const summaries: CrmPatientSummary[] = [];
    for (const resource of context.consentPatients ?? []) {
      const mapped = mapFhirPatientToCrmPatientSummary(resource);
      if (!mapped) continue;
      summaries.push(mapped);
      patientById.set(mapped.patientId, resource);
    }

    const attended = mapPatientEntriesToCrmPatientSummaries(context.patients);
    const remaining = consentPatientsNotAttended(summaries, attended);
    const result: ConsentNotAttendedRow[] = [];
    for (const summary of remaining) {
      const patient = patientById.get(summary.patientId);
      if (!patient) continue;
      result.push({
        patientId: summary.patientId,
        name: summary.officialName,
        patient,
      });
    }
    return result;
  }, [loading, context]);

  return { rows, loading };
}
